import Deferred from './Deferred';
import util from './Util'
import navi from './Navi';


/**
 * Represents a scheduler timeout.
 *
 * You should not instantiate this yourself.
 *
 * @class Scheduler.Timeout
 * @param {String} component Component name
 * @param {Function} callback Callback to call
 * @param {Number} milliseconds Timeout time in milliseconds
 * @param {Boolean} persistent Should this timeout survive navigation
 * @param {Array} parameters Optional parameters
 * @param {Boolean} interval Is the timeout of interval type
 * @constructor
 * @extends Deferred
 * @module Core
 */

export class Timeout extends Deferred {
    private _id;
    private _counter = 0;
    private _alive = true;

    constructor(private _component, private  _callback, private _milliseconds, private _persistent, private _parameters, private _interval) {
        super();

        if (this._interval) {
            this._id = window.setInterval(() => {
                this._counter++;

                if (util.isFunction(_callback)) {
                    _callback.apply(this, _parameters);
                }
            }, _milliseconds);
        } else {
            this._id = window.setTimeout(() => {
                this._alive = false;

                if (util.isFunction(_callback)) {
                    _callback.apply(self, _parameters);
                }

                this.resolve.apply(this, [this, 'fired']);
            }, _milliseconds);
        }
    }

    /**
     * Returns whether given timeout is still alive.
     *
     * @method isAlive
     * @for Scheduler.Timeout
     * @return {Boolean}
     */
    isAlive() {
        return this._alive;
    };

    /**
     * Returns whether given timeout is persistent across navigation.
     *
     * @method isPersistent
     * @for Scheduler.Timeout
     * @return {Boolean}
     */
    isPersistent() {
        return this._persistent;
    };

    /**
     * Returns whether given timeout is of interval type.
     *
     * @method isInterval
     * @for Scheduler.Timeout
     * @return {Boolean}
     */
    isInterval() {
        return this._interval;
    };

    /**
     * Cancels the timeout.
     *
     * @method cancel
     * @for Scheduler.Timeout
     */
    cancel() {
        if (!this._alive) {
            return;
        }

        if (this._interval) {
            window.clearInterval(this._id);
        } else {
            window.clearTimeout(this._id);
        }

        this.resolve(this, 'cancel');

        this._alive = false;
    }
}


/**
 * Provides functionality for scheduling timeout and interval events.
 *
 * @class Scheduler
 * @constructor
 * @module Core
 */
export class Scheduler {
    _timeouts = {};
    _intervals = {};


    /**
     * Initiates the components.
     *
     * @method init
     * @return {Scheduler} Self
     */
    init() {
        var self = this;

        navi.bind(navi.Event.START_NAVIGATE, function () {
            // does not clear persistent timeouts
            self.clear(null, false);
        });

        return this;
    }

    /**
     * Sets a timeout.
     *
     * By default, timeouts are cleared on every navigation event. This ensures that some timeouts don't remain active
     * when the view has already changed. Should you want to keep your event while navigating, set persistent to true.
     *
     * @method setTimeout
     * @for Scheduler
     * @param {String} [component=general] Optional components name
     * @param {Function} callback Callback to call
     * @param {Number} milliseconds Timeout time in milliseconds
     * @param {Boolean} [persistent=false] Should this timeout survive navigation
     * @param {Array} [parameters] Optional parameters
     * @return {Scheduler.Timeout}
     */
    setTimeout(component, callback, milliseconds?, persistent?, parameters?, isInterval = false) {
        if (util.isFunction(component)) {
            component = 'general';
            callback = arguments[0];
            milliseconds = arguments[1];
            persistent = arguments[2];
            parameters = arguments[3];
        }

        persistent = util.isUndefined(persistent) ? false : util.bool(persistent);
        parameters = parameters || [];

        var isInterval = arguments.length === 6 && arguments[5] === true,
            timeout = new Timeout(component, callback, milliseconds, persistent, parameters, isInterval);

        if (util.isUndefined(this._timeouts[component])) {
            this._timeouts[component] = [];
        }

        this._timeouts[component].push(timeout);

        this._purge();

        return timeout;
    }

    /**
     * Sets an interval.
     *
     * By default, intervals are cleared on every navigation event. This ensures that some intervals don't remain active
     * when the view has already changed. Should you want to keep your event while navigating, set persistent to true.
     *
     * @method setInterval
     * @param {String} [component=general] Optional components name
     * @param {Function} callback Callback to call
     * @param {Number} milliseconds Interval time in milliseconds
     * @param {Boolean} [persistent=false] Should this timeout survive navigation
     * @param {Array} [parameters] Optional parameters
     * @return {Scheduler.Timeout}
     */
    setInterval(component, callback, milliseconds, persistent, parameters) {
        return this.setTimeout(component, callback, milliseconds, persistent, parameters, true);
    }

    /**
     * Cancels and clears all timeouts.
     *
     * @method clearTimeouts
     * @param {String|null} [type=null] Set this if you wish to clear specific components
     * @param {Boolean} [includingPersistent=true] Should persistent timeouts be cleared too
     */
    clearTimeouts(type, includingPersistent) {
        type = type || null;
        includingPersistent = util.isUndefined(includingPersistent) ? true : util.bool(includingPersistent);

        var isInterval = arguments.length === 3 && arguments[2] === true,
            component,
            i;

        for (component in this._timeouts) {
            if (type !== null && component !== type) {
                continue;
            }

            for (i = 0; i < this._timeouts[component].length; i++) {
                if (
                    (includingPersistent || !this._timeouts[component][i].isPersistent())
                    && this._timeouts[component][i].isInterval() === isInterval
                ) {
                    this._timeouts[component][i].cancel();
                }

                this._timeouts[component].splice(i, 1);
            }

            if (this._timeouts[component].length === 0) {
                delete this._timeouts[component];
            }
        }
    }

    /**
     * Cancels and clears all intervals.
     *
     * @method clearIntervals
     * @param {String|null} [type=null] Set this if you wish to clear specific components
     * @param {Boolean} [includingPersistent=true] Should persistent intervals be cleared too
     */
    clearIntervals(type, includingPersistent) {
        this.clearTimeouts(type, includingPersistent);
    }

    /**
     * Cancels and clears all timeouts and intervals.
     *
     * @method clear
     * @param {String|null} [type=null] Set this if you wish to clear specific components
     * @param {Boolean} [includingPersistent=true] Should persistent timeouts be cleared too
     */
    clear(type, includingPersistent) {
        this.clearTimeouts(type, includingPersistent);
        this.clearIntervals(type, includingPersistent);
    }

    /**
     * Cancels and clears all timeouts.
     *
     * @method _purge
     * @private
     */
    _purge() {
        var component,
            i;

        for (component in this._timeouts) {
            for (i = 0; i < this._timeouts[component].length; i++) {
                if (!this._timeouts[component][i].isAlive()) {
                    this._timeouts[component].splice(i, 1);
                }
            }
        }
    }

    static Timeout = Timeout;
}


export default new Scheduler();


// WEBPACK FOOTER //
// ./src/main/ui/core/application/Scheduler.ts